import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  RESELLER_MEMBER_ROLES,
  RESELLER_MEMBER_ROLE_LABELS,
  defaultLinkRole,
  type ResellerMatchCandidate,
  type ResellerMemberRole,
} from "@xop/dashboard-shared";
import { useState } from "react";
import type { JSX } from "react";

import { ApiError } from "@/lib/api";

/**
 * Confirm "Activate as THIS existing reseller" (spec §5.2). Shows what the
 * link hands over (that reseller's active member count, and therefore its
 * whole customer/lead subtree) and asks for the new person's role. The
 * default is `salesperson` when the company already has members and `owner`
 * when it has none — rendered with `key={candidate.reseller_id}` by the page
 * so the default resets per candidate.
 */
export function LinkResellerDialog({
  candidate,
  applicantEmail,
  isSubmitting,
  error,
  onClose,
  onConfirm,
}: {
  candidate: ResellerMatchCandidate | null;
  applicantEmail: string;
  isSubmitting: boolean;
  error: ApiError | null;
  onClose: () => void;
  onConfirm: (role: ResellerMemberRole) => Promise<void>;
}): JSX.Element {
  const [role, setRole] = useState<ResellerMemberRole>(candidate ? defaultLinkRole(candidate) : "owner");

  const handleClose = (): void => {
    if (!isSubmitting) onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    try {
      await onConfirm(role);
      onClose();
    } catch {
      // The page surfaces the ApiError through `error`; stay open so the
      // reviewer reads it and can pick a different verb.
    }
  };

  return (
    <Dialog open={candidate !== null} onClose={handleClose} fullWidth PaperProps={{ sx: { maxWidth: 520 } }}>
      <DialogTitle>Activate as an existing reseller?</DialogTitle>
      <DialogContent>
        {candidate && (
          <>
            <DialogContentText sx={{ fontSize: 14 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>{applicantEmail}</Box> will be added to{" "}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {candidate.name}
                {candidate.code ? ` (${candidate.code})` : ""}
              </Box>{" "}
              and will see that reseller&rsquo;s {candidate.customer_count} customers and {candidate.lead_count} leads. This is
              the identity decision — it is recorded with your name.
            </DialogContentText>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1.5 }}>
              {candidate.active_members > 0
                ? `${candidate.name} already has ${candidate.active_members} active ${candidate.active_members === 1 ? "member" : "members"}, so the new person joins as a salesperson unless you choose otherwise.`
                : `${candidate.name} has no active members, so the applicant becomes its owner.`}
            </Typography>
            <TextField
              select
              size="small"
              label="Role"
              value={role}
              onChange={(e) => setRole(e.target.value as ResellerMemberRole)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "data-cy": "link-role" }}
              sx={{ mt: 2, minWidth: 220 }}
              helperText="Both roles see the whole reseller subtree — the role records who does what."
            >
              {RESELLER_MEMBER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {RESELLER_MEMBER_ROLE_LABELS[r]}
                </option>
              ))}
            </TextField>
            {error && (
              <Box role="alert" sx={{ mt: 2, borderRadius: 1, border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.4), bgcolor: (t) => alpha(t.palette.error.main, 0.12), px: 1.5, py: 1 }}>
                <Typography sx={{ fontSize: 13, color: "error.main" }}>{error.message}</Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleConfirm()} disabled={isSubmitting || candidate === null} data-cy="link-confirm">
          {isSubmitting ? "Activating…" : "Activate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
